import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './LoginSubmission.css';
import { toast, ToastContainer } from 'react-toastify';
import { useWallet } from '../../context/WalletContext';
import { API_BASE } from '../../utils/api';
import { isValidEthAddress } from '../../utils/validation';
import { FLASH_SOLANA_MINT, FLASH_SOLSCAN_URL, formatFlashBalance } from '../../utils/token';
import {
    buildMigrationMessage,
    bytesToBase64,
    canFallbackToMemo,
    getSubmitErrorMessage,
    isUserRejectedSignature
} from '../../utils/signature';
import { signMigrationMemo } from '../../utils/memoProof';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { openWalletPicker } from '../../utils/mobileWallet';

const LoginSubmission = () => {
    const {
        status,
        formStatus,
        setFormStatus,
        registrationLocked,
        registeredEthAddress,
        setRegisteredEthAddress,
        connectedAddress,
        isConnected,
        balance
    } = useWallet();
    const { signMessage, signTransaction, publicKey, select } = useSolanaWallet();
    const { setVisible } = useWalletModal();
    const [ethAddress, setEthAddress] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const isRegistered = formStatus;
    const canUpdateEth = isRegistered && !registrationLocked;
    const showSubmissionForm = !isRegistered || canUpdateEth;

    const openWallet = () => {
        openWalletPicker({ select, setVisible });
    };

    const handleSubmit = async () => {
        if (!connectedAddress || !ethAddress) {
            if (!connectedAddress) {
                openWallet();
            }
            toast.error('Please connect your Solana wallet and enter an Ethereum address', { autoClose: 3000 });
            return;
        }

        if (!isValidEthAddress(ethAddress)) {
            toast.error('Please enter a valid Ethereum address', { autoClose: 3000 });
            return;
        }

        if (!signMessage && !signTransaction) {
            toast.error('This wallet cannot sign the migration request', { autoClose: 4000 });
            return;
        }

        try {
            setSubmitting(true);
            const timestamp = Date.now();
            const destination = ethAddress.trim();
            const message = buildMigrationMessage({
                holder_address: connectedAddress,
                eth_address: destination,
                timestamp
            });
            const payload = {
                holder_address: connectedAddress,
                eth_address: destination,
                timestamp
            };
            try {
                if (!signMessage) {
                    throw new Error('This wallet cannot sign messages');
                }
                const signed = await signMessage(new TextEncoder().encode(message));
                payload.signature = bytesToBase64(signed);
            } catch (signError) {
                if (!canFallbackToMemo(signError) || !signTransaction || !publicKey) {
                    throw signError;
                }
                toast.info('This wallet cannot sign messages. Approve the migration transaction — it is not sent.', {
                    autoClose: 4000
                });
                payload.signed_tx = await signMigrationMemo({
                    signTransaction,
                    publicKey,
                    message
                });
            }
            const response = await axios.post(`${API_BASE}add-address`, payload);
            setEthAddress('');
            setFormStatus(true);
            setRegisteredEthAddress(destination.trim());
            const updated = Boolean(response.data && response.data.updated);
            toast.success(
                updated ? 'Ethereum address updated' : 'Submitted Successfully',
                { autoClose: 2500 }
            );
            console.log('Response:', response.data);
        } catch (error) {
            if (!isUserRejectedSignature(error)) {
                console.error('Error submitting address:', error);
            }
            toast.error(getSubmitErrorMessage(error), { autoClose: 6000 });
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!isConnected) {
            setFormStatus(false);
        }
    }, [isConnected, setFormStatus]);

    return (
        <>
            <ToastContainer />
            <div className='flex justify-center items-center flex-col my-[70px] p-2'>
                <div>
                    <div className='flex justify-center items-center gap-2'>
                        <img className='w-[47px] h-[40px] max-[700px]:w-[37px] max-[700px]:h-[30px]' src="../../assets/logo.png" alt="" />
                        <h5 className='text-3xl font-bold max-[700px]:text-xl'>1 FLASH = 1 FLASH on ETH</h5>
                    </div>
                    <h2 className='text-center text-5xl font-bold my-9 text-[#fcbf07] max-[700px]:text-2xl max-[700px]:my-5'>Migration Solana To ETH</h2>
                    <p className='text-center text-2xl max-w-[700px] opacity-60 max-[700px]:text-lg'>Connect Your Solana Wallet Which Contains Flash Then Enter Your Ethereum Wallet To Receive Your Airdrop</p>
                    <p className='text-center text-sm max-w-[700px] opacity-60 mt-4 break-all'>
                        Flash Solana token:{' '}
                        <a
                            href={FLASH_SOLSCAN_URL}
                            target='_blank'
                            rel='noreferrer'
                            className='text-[#fcbf07] underline'
                        >
                            {FLASH_SOLANA_MINT}
                        </a>
                    </p>
                </div>
                <div className='bg-[#fcbf07] h-[2px] w-full max-w-[1400px] my-[50px] opacity-60'></div>
                {isRegistered && registrationLocked && (
                    <div className='flex justify-center items-center flex-col h-[250px] my-[70px] p-2'>
                        <div className="registered-message">
                            <img src="../../assets/Vector.svg" alt="Success" className="registered-icon " />
                            <h2 className='registered-text'>You are registered</h2>
                            <p className='text-center text-sm max-w-[520px] opacity-70 mt-4 px-4'>
                                This migration entry is locked and cannot be changed on the portal.
                            </p>
                        </div>
                    </div>
                )}
                {showSubmissionForm &&
                    <div className='w-full flex justify-center items-center flex-col gap-10'>
                        {canUpdateEth && (
                            <div className="registered-message !my-0">
                                <img src="../../assets/Vector.svg" alt="Success" className="registered-icon " />
                                <h2 className='registered-text'>You are registered</h2>
                            </div>
                        )}
                        <div className='flex items-center gap-8'>
                            <img className='w-[60px] h-[60px]' src="../../assets/c.png" alt="Solana" />
                            <img className='w-[35px] h-[35px]' src="../../assets/arrow.png" alt="" />
                            <img className='w-[60px] h-[60px]' src="../../assets/eth.png" alt="Ethereum" />
                        </div>

                        <div className='w-full max-w-[1000px] flex justify-center items-center flex-col'>
                            <h6 className='text-center mb-5 text-xl max-w-[700px] opacity-60'>
                                {canUpdateEth
                                    ? 'Update your Ethereum address (Solana signature required)'
                                    : 'Submit Your Ethereum Address'}
                            </h6>
                            {canUpdateEth && registeredEthAddress && (
                                <p className='text-center mb-4 text-sm max-w-[700px] opacity-60 break-all'>
                                    Current address: {registeredEthAddress}
                                </p>
                            )}
                            <div className="w-full max-w-[1000px] flex items-center justify-center gap-3 px-6 border-gray-300 rounded-[50px] bg-[#1b1b1b] b-primary">
                                <img className='w-[40px] h-[40px] shrink-0' src="../../assets/eth.png" alt="Ethereum" />
                                <input
                                    type="text"
                                    placeholder="Enter Your Ethereum Address"
                                    value={ethAddress}
                                    onChange={(e) => setEthAddress(e.target.value)}
                                    className="flex-1 min-w-0 opacity-60 outline-none rounded-xl py-5 bg-transparent placeholder:text-[#86888C] text-2xl font-semibold max-[600px]:text-sm break-all"
                                />
                            </div>
                            <button
                                type='button'
                                disabled={submitting}
                                onClick={handleSubmit}
                                className='themeBtn-lg mt-[15px] text-2xl font-semibold min-h-[56px]'
                            >
                                {submitting ? 'Signing...' : (canUpdateEth ? 'Sign & update' : 'Submit')}
                            </button>
                        </div>

                        <div>
                            {connectedAddress && (
                                <p className={`font-bold ${status ? 'text-[#35DD00]' : 'text-[#FD2020]'}`}>
                                    {status
                                        ? `You Are Eligible — ${formatFlashBalance(balance)} FLASH`
                                        : 'You Are Not Eligible'}
                                </p>
                            )}
                        </div>
                    </div>
                }
            </div>
        </>
    );
};

export default LoginSubmission;
